import React, { useCallback, useState } from 'react';
import { Box, Typography, Button, Dialog, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone } from 'react-dropzone';

const MAX_FILE_SIZE = 1024 * 1024; // 1 MB

const UploadRow = ({ label, file, handler, isOptional = false, onPreview, tanValue = '', onTanChange }) => {
  const displayLabel = isOptional ? `${label} (Optional)` : label;

  const [openModal, setOpenModal] = useState(false);
  const [tanError, setTanError] = useState('');
  const [fileError, setFileError] = useState('');

  const sampleLinks = {
    "GST Certificate": "/sample_gst_registration_certificate.jpg",
    "PAN Card": "/sample_pan.jpg",
    "TAN Certificate": "/sample_tan.jpg",
    "TAN Certificate - Proof of TAN number should be legitimate": "/sample_tan.jpg",
    "TAN Certificate (Optional) - Proof of TAN number should be legitimate": "/sample_tan.jpg",
    "Certificate of Incorporation (if any)": "/sample_certificate_of_incorporation.png",
    "Cancelled Cheque": "/sample_cancelled_cheque.png"
  };

  const sampleUrl = sampleLinks[label];

  const processFile = useCallback(
    (rawFile) => {
      setFileError('');
      if (!rawFile) return;
      if (rawFile.size > MAX_FILE_SIZE) {
        const sizeMB = (rawFile.size / (1024 * 1024)).toFixed(2);
        setFileError(`File is ${sizeMB} MB. Maximum allowed size is 1 MB.`);
        return;
      }
      handler({ target: { files: [rawFile] } });
    },
    [handler]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles?.length > 0) {
        processFile(acceptedFiles[0]);
      }
    },
    [processFile]
  );

  const handleFileInput = useCallback(
    (e) => {
      const rawFile = e.target.files?.[0];
      if (rawFile) processFile(rawFile);
    },
    [processFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'image/*': [], 'application/pdf': [] }
  });

  // Validation for TAN Number
  const validateTan = (value) => {
    const tanRegex = /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/;
    if (!value) {
      setTanError('');
      return true;
    }
    if (!tanRegex.test(value)) {
      setTanError('TAN must be 10 characters (e.g., ABCD12345E)');
      return false;
    }
    setTanError('');
    return true;
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3
        }}
      >
        {/* Preview Box */}
        <Box
          {...getRootProps()}
          sx={{
            width: 100,
            height: 100,
            border: 2,
            borderStyle: 'dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 1,
            overflow: 'hidden',
            cursor: 'pointer',
            position: 'relative',
            bgcolor: isDragActive ? 'primary.light' : 'transparent',
            transition: '0.2s'
          }}
          onClick={(e) => {
            if (!isDragActive && file && onPreview) {
              e.stopPropagation();
              onPreview(label, file);
            }
          }}
        >
          <input {...getInputProps()} />
          {file ? (
            file.type.startsWith('image/') ? (
              <img
                src={URL.createObjectURL(file)}
                alt={label}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <iframe
                src={URL.createObjectURL(file)}
                title={label}
                width="100%"
                height="100%"
                style={{ border: 0, pointerEvents: 'none' }}
              />
            )
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'grey.100',
                color: 'text.secondary',
                fontSize: 10,
                textAlign: 'center',
                p: 0
              }}
            >
              {isDragActive ? 'Drop here' : 'Drag & drop or click'}
            </Box>
          )}
        </Box>

        {/* File Info + Buttons */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>{displayLabel}:</strong> {file?.name || ''}
          </Typography>
          <Button
            variant={file ? 'text' : 'outlined'}
            component="label"
            sx={{ p: "5px", fontSize: 12, fontWeight: "600", textTransform: 'none' }}
          >
            {file ? 'Replace' : `Upload ${label}`}
            <input
              type="file"
              hidden
              accept="image/*,application/pdf"
              onChange={handleFileInput}
            />
          </Button>

          {sampleUrl && (
            <Button
              sx={{
                ml: 2,
                fontSize: '10px',
                textTransform: 'none',
                backgroundColor: '#1976d2',
                color: '#fff',
                p: '5px',
                borderRadius: '4px'
              }}
              onClick={() => setOpenModal(true)}
            >
              Sample Format
            </Button>
          )}

          {fileError && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              {fileError}
            </Typography>
          )}

          {/* Show TAN number field for TAN Certificate - always show if no extracted value or if user wants to override */}
          {label.startsWith("TAN Certificate") && (
            <TextField
              label="Enter TAN Number (Required if extraction fails)"
              value={tanValue}
              onChange={(e) => {
                const val = e.target.value.toUpperCase();
                onTanChange?.(val);   // updates parent state
                validateTan(val);     // validation
              }}
              error={!!tanError}
              helperText={tanError || "TAN will be auto-extracted if possible, otherwise enter manually"}
              size="small"
              sx={{ mt: 2, width: "60%" }}
              inputProps={{ maxLength: 10 }}
              placeholder="ABCD12345E"
            />
          )}

        </Box>
      </Box>

      {/* Modal for Preview */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          Sample Preview
          <IconButton onClick={() => setOpenModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {sampleUrl?.endsWith('.pdf') ? (
            <iframe
              src={sampleUrl}
              title="Sample Preview"
              width="100%"
              height="500px"
              style={{ border: 'none' }}
            />
          ) : (
            <img
              src={sampleUrl}
              alt="Sample"
              style={{ width: '100%', maxHeight: '500px', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UploadRow;
